














































































































































































































































































































































import { defineComponent, computed } from '@vue/composition-api';
import {
    PageHeader,
    PageBody,
    ArrowRightIcon,
    FiatAmount,
    Tooltip,
    InfoCircleSmallIcon,
    CircleSpinner,
    CrossIcon,
    Identicon,
} from '@nimiq/vue-components';
import { TransactionState } from '@nimiq/electrum-client';
import { RefundSwapRequest, SignedBtcTransaction } from '@nimiq/hub-api';
import { SwapAsset, getAssets } from '@nimiq/fastspot-api';
import { SettlementStatus } from '@nimiq/oasis-api';
import { RouteName, useRouter } from '@/router';
import { useI18n } from '@/lib/useI18n';
import { nextTick } from '@/lib/nextTick';
import Amount from '../Amount.vue';
import FiatConvertedAmount from '../FiatConvertedAmount.vue';
import Modal from './Modal.vue';
// import HistoricValueIcon from '../icons/HistoricValueIcon.vue';
import BlueLink from '../BlueLink.vue';
import Avatar from '../Avatar.vue';
import BitcoinIcon from '../icons/BitcoinIcon.vue';
import InteractiveShortAddress from '../InteractiveShortAddress.vue';
import GroundedArrowUpIcon from '../icons/GroundedArrowUpIcon.vue';
import GroundedArrowDownIcon from '../icons/GroundedArrowDownIcon.vue';
import SwapMediumIcon from '../icons/SwapMediumIcon.vue';
import BankIcon from '../icons/BankIcon.vue';
import { useBtcTransactionsStore } from '../../stores/BtcTransactions';
import { useBtcAddressStore } from '../../stores/BtcAddress';
import { useBtcLabelsStore } from '../../stores/BtcLabels';
import { useAccountStore, AccountType } from '../../stores/Account';
import { useBtcNetworkStore } from '../../stores/BtcNetwork';
import { twoDigit } from '../../lib/NumberFormatting';
import { CryptoCurrency, FIAT_PRICE_UNAVAILABLE } from '../../lib/Constants';
import { isProxyData, ProxyType } from '../../lib/ProxyDetection';
import { SwapBtcData } from '../../stores/Swaps';
import { useTransactionsStore } from '../../stores/Transactions';
import { useAddressStore } from '../../stores/Address';
import { estimateFees } from '../../lib/BitcoinTransactionUtils';
import { refundSwap } from '../../hub';
import { sendTransaction } from '../../electrum';
import { explorerTxLink } from '../../lib/ExplorerUtils';
import { assetToCurrency } from '../../lib/swap/utils/Assets';
import TransactionDetailOasisPayoutStatus from '../TransactionDetailOasisPayoutStatus.vue';
import { useUsdcTransactionsStore, Transaction as UsdcTransaction } from '../../stores/UsdcTransactions';
import { useUsdtTransactionsStore, Transaction as UsdtTransaction } from '../../stores/UsdtTransactions';
import { useBtcTransactionInfo } from '../../composables/useBtcTransactionInfo';
import UsdcIcon from '../icons/UsdcIcon.vue';
import UsdtIcon from '../icons/UsdtIcon.vue';

export default defineComponent({
    name: 'btc-transaction-modal',
    props: {
        hash: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const router = useRouter();
        const { $t } = useI18n();
        const constants = { FIAT_PRICE_UNAVAILABLE };
        const transaction = computed(() => useBtcTransactionsStore().state.transactions[props.hash]);

        const {
            setRecipientLabel,
            getRecipientLabel,
            setSenderLabel,
            getSenderLabel,
        } = useBtcLabelsStore();

        const state = computed(() => transaction.value.state);

        // Note that as the transaction modal is typically opened from the active account's transaction history, we base
        // our calculations here on the active account and its addresses. This yields wrong results if the opened
        // transaction modal (e.g. opened via url) does not belong to the active account but saves us scanning through
        // all accounts in the common case.
        const {
            amountReceived,
            amountSent,
            isIncoming,
            isCancelledSwap,
            inputsSent,
            peerLabel,
            outputsReceived,
            outputsSent,
            swapData,
            swapInfo,
            fiat,
        } = useBtcTransactionInfo(transaction);

        const ownAddresses = computed(() => (isIncoming.value
            ? outputsReceived.value.map((output) => output.address || output.script)
            : inputsSent.value.map((input) => input.address || input.script)
        ).filter((address, index, array) => array.indexOf(address) === index)); // dedupe

        const swapTransaction = computed(() => {
            if (!swapData.value) return null;

            if (swapData.value.asset === SwapAsset.NIM) {
                let swapTx = useTransactionsStore().state.transactions[swapData.value.transactionHash];
                if (swapTx?.relatedTransactionHash) {
                    // Avoid showing the swap proxy, instead show our related address.
                    swapTx = useTransactionsStore().state.transactions[swapTx.relatedTransactionHash];
                }
                return swapTx || null;
            }

            if (swapData.value.asset === SwapAsset.USDC || swapData.value.asset === SwapAsset.USDC_MATIC) {
                return useUsdcTransactionsStore().state.transactions[swapData.value.transactionHash] || null;
            }

            if (swapData.value.asset === SwapAsset.USDT_MATIC) {
                return useUsdtTransactionsStore().state.transactions[swapData.value.transactionHash] || null;
            }

            return null;
        });

        const usesNimSwapProxy = computed(() => {
            if (!swapTransaction.value || !('validityStartHeight' in swapTransaction.value)) return false;
            const swapPeerAddress = isIncoming.value
                ? swapTransaction.value.sender
                : swapTransaction.value.recipient;
            // Note that we don't only test for the swap proxy detection extra data here as the swap tx holds htlc data
            // instead. Only the related tx holds the proxy identifying extra data.
            return isProxyData(swapTransaction.value.data.raw, ProxyType.HTLC_PROXY)
                || swapTransaction.value.relatedTransactionHash
                || !useAddressStore().state.addressInfos[swapPeerAddress]; // not one of our addresses -> proxy
        });

        // Data
        const data = computed(() => {
            if (isCancelledSwap.value) {
                return isIncoming.value ? $t('HTLC Refund') : $t('HTLC Creation');
            }

            return '';
        });

        // Peer
        const peerAddresses = computed(() => {
            if (swapData.value) {
                if (swapData.value.asset === SwapAsset.NIM && swapTransaction.value) {
                    if (usesNimSwapProxy.value
                        && !(swapTransaction.value as Extract<
                            typeof swapTransaction.value,
                            { relatedTransactionHash?: string }
                        >).relatedTransactionHash) {
                        // Avoid displaying proxy address identicon until we know related address.
                        return [''];
                    }
                    return isIncoming.value ? [swapTransaction.value.sender] : [swapTransaction.value.recipient];
                }

                if (
                    (swapData.value.asset === SwapAsset.USDC || swapData.value.asset === SwapAsset.USDC_MATIC)
                    && swapTransaction.value
                ) {
                    const swapTx = swapTransaction.value as UsdcTransaction;
                    return isIncoming.value ? [swapTx.sender] : [swapTx.recipient];
                }

                if (swapData.value.asset === SwapAsset.USDT_MATIC && swapTransaction.value) {
                    const swapTx = swapTransaction.value as UsdtTransaction;
                    return isIncoming.value ? [swapTx.sender] : [swapTx.recipient];
                }

                if (swapData.value.asset === SwapAsset.EUR) return [swapData.value.iban || ''];
            }

            return (isIncoming.value
                ? transaction.value.inputs.map((input) => input.address || input.script)
                : outputsSent.value.map((output) => output.address || output.script)
            ).filter((address, index, array) => array.indexOf(address) === index); // dedupe
        });

        const senderLabelAddress = computed(() => isIncoming.value
            && (ownAddresses.value.find((address) => !!getSenderLabel.value(address)) || false));
        const recipientLabelAddress = computed(() => !isIncoming.value
            && (peerAddresses.value.find((address) => !!getRecipientLabel.value(address)) || false));

        // Date
        const date = computed(() => transaction.value.timestamp && new Date(transaction.value.timestamp * 1000));
        const datum = computed(() => date.value && date.value.toLocaleDateString());
        const time = computed(() => date.value
            && `${twoDigit(date.value.getHours())}:${twoDigit(date.value.getMinutes())}`);

        const { height: blockHeight, timestamp: blockTimestamp } = useBtcNetworkStore();
        const confirmations = computed(() =>
            transaction.value.blockHeight ? blockHeight.value - transaction.value.blockHeight + 1 : 0);

        const showRefundButton = computed(() => !isIncoming.value
            && swapInfo.value?.in?.asset === SwapAsset.BTC
            && (swapInfo.value.in.htlc?.timeoutTimestamp || Number.POSITIVE_INFINITY) <= blockTimestamp.value
            && !swapInfo.value.out
            // Only display the refund button for Ledger accounts as the Keyguard signs automatic refund transaction.
            && useAccountStore().activeAccountInfo.value?.type === AccountType.LEDGER,
        );

        const blockExplorerLink = computed(() =>
            explorerTxLink(CryptoCurrency.BTC, transaction.value.transactionHash));

        async function refundHtlc() {
            const swapIn = swapInfo.value!.in as SwapBtcData;
            const htlcDetails = swapIn.htlc!;
            const htlcOutput = outputsSent.value[0];

            // eslint-disable-next-line no-async-promise-executor
            const requestPromise = new Promise<Omit<RefundSwapRequest, 'appName'>>(async (resolve) => {
                const assets = await getAssets();
                const { feePerUnit } = assets[SwapAsset.BTC];
                // 102 extra weight units for BTC HTLC refund tx
                const fee = estimateFees(1, 1, feePerUnit, 102);

                const request: Omit<RefundSwapRequest, 'appName'> = {
                    accountId: useAccountStore().activeAccountId.value!,
                    refund: {
                        type: SwapAsset.BTC,
                        input: {
                            transactionHash: transaction.value.transactionHash,
                            outputIndex: htlcOutput.index,
                            outputScript: htlcOutput.script,
                            value: htlcOutput.value,
                            witnessScript: htlcDetails.script,
                        },
                        output: {
                            address: useBtcAddressStore().availableExternalAddresses.value[0],
                            value: htlcOutput.value - fee,
                        },
                        refundAddress: htlcDetails.refundAddress, // My address, must be refund address of HTLC
                    },
                };

                resolve(request);
            });

            const tx = await refundSwap(requestPromise);
            if (!tx) return;
            const plainTx = await sendTransaction(tx as SignedBtcTransaction);
            await nextTick();
            router.replace({
                name: RouteName.BtcTransaction,
                params: { hash: plainTx.transactionHash },
            });
        }

        return {
            transaction,
            constants,
            state,
            TransactionState,
            datum,
            time,
            data,
            outputsReceived,
            amountReceived,
            inputsSent,
            amountSent,
            fiat,
            isIncoming,
            peerAddresses,
            peerLabel,
            ownAddresses,
            confirmations,
            // peerIsContact,
            // setContact,
            blockExplorerLink,
            senderLabelAddress,
            recipientLabelAddress,
            setSenderLabel,
            setRecipientLabel,
            swapInfo,
            swapData,
            swapTransaction,
            SwapAsset,
            usesNimSwapProxy,
            isCancelledSwap,
            showRefundButton,
            refundHtlc,
            SettlementStatus,
            assetToCurrency,
            RouteName,
        };
    },
    components: {
        Amount,
        FiatConvertedAmount,
        ArrowRightIcon,
        Avatar,
        BitcoinIcon,
        PageBody,
        PageHeader,
        Modal,
        InteractiveShortAddress,
        FiatAmount,
        Tooltip,
        InfoCircleSmallIcon,
        CircleSpinner,
        CrossIcon,
        // HistoricValueIcon,
        BlueLink,
        GroundedArrowUpIcon,
        GroundedArrowDownIcon,
        BankIcon,
        Identicon,
        SwapMediumIcon,
        TransactionDetailOasisPayoutStatus,
        UsdcIcon,
        UsdtIcon,
    },
});
